import {
  ChartData,
  ChartMetaInfo,
  ChartTemplate,
  ChartTemplateContent,
  IExternalSaveLoadAdapter,
  LineToolsAndGroupsLoadRequestContext,
  LineToolsAndGroupsLoadRequestType,
  LineToolsAndGroupsState,
  StudyTemplateData,
  StudyTemplateMetaInfo,
} from '@frontends/feature-charting-library/charting_library';
import { applyThemeToSavedChart } from '@frontends/feature-tv-parts';
import { v4 as createUUID } from 'uuid';
import { $axios } from '../../../hooks/useAxiosInterceptors';

type ReplayTheme = 'dark' | 'light';

interface CompoundChartContents {
  charts: Record<string, ChartData>;
  chartTemplates: Record<string, ChartTemplateContent>;
  drawings: Record<string, Record<string, unknown>>;
  drawingTemplates: Record<string, string>;
  studyTemplates: Record<string, StudyTemplateData>;
}

const CHART_CONTENTS_NAME = 'chart_contents';

function drawingTemplateKey(toolName: string, templateName: string): string {
  return `${toolName}/${templateName}`;
}

function drawingKey(layoutId: string | undefined, chartId: string | number): string {
  return `${layoutId || ''}/${chartId}`;
}

async function fetchChartContents(persistenceUrl: string, login: number): Promise<CompoundChartContents | null> {
  if (!persistenceUrl) {
    console.warn('[ReplaySaveLoadAdapter] persistenceUrl is empty — skip fetch');

    return null;
  }

  const url = `${persistenceUrl}/api/v1/settings/${login}/${CHART_CONTENTS_NAME}`;

  try {
    const response = await $axios.get(url);
    const value = response.data?.value;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const parsed = value as Partial<CompoundChartContents>;

      return {
        charts: parsed.charts ?? {},
        studyTemplates: parsed.studyTemplates ?? {},
        drawingTemplates: parsed.drawingTemplates ?? {},
        chartTemplates: parsed.chartTemplates ?? {},
        drawings: parsed.drawings ?? {},
      };
    }

    return null;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;

    if (status === 404) {
      return null;
    }

    console.warn(
      `[ReplaySaveLoadAdapter] failed to fetch chart_contents for ${login}, status=${status ?? 'unknown'}`,
      err,
    );

    return null;
  }
}

export interface ReplaySaveLoadAdapterInitOpts {
  bypass?: boolean;
  isApplyEnabled?: boolean;
  persistenceUrl?: string;
}

export class ReplaySaveLoadAdapter implements IExternalSaveLoadAdapter {
  private login: number;
  private theme: ReplayTheme;
  private state: CompoundChartContents;

  constructor(login: number, theme: ReplayTheme, initial?: CompoundChartContents) {
    this.login = login;
    this.theme = theme;
    this.state = initial ?? {
      charts: {},
      studyTemplates: {},
      drawingTemplates: {},
      chartTemplates: {},
      drawings: {},
    };
  }

  static async initialize(
    login: number,
    theme: ReplayTheme,
    opts: ReplaySaveLoadAdapterInitOpts = {},
  ): Promise<ReplaySaveLoadAdapter> {
    const { bypass = false, persistenceUrl = '', isApplyEnabled = false } = opts;

    if (!isApplyEnabled) {
      return new ReplaySaveLoadAdapter(login, theme);
    }

    if (bypass) {
      return new ReplaySaveLoadAdapter(login, theme);
    }

    const fetched = await fetchChartContents(persistenceUrl, login);
    const adapter = new ReplaySaveLoadAdapter(login, theme, fetched ?? undefined);

    if (fetched) {
      adapter.applyThemeToCharts();
    }

    return adapter;
  }

  private applyThemeToCharts(): void {
    for (const chart of Object.values(this.state.charts)) {
      if (typeof chart.content === 'string') {
        chart.content = applyThemeToSavedChart(chart.content, this.theme);
      }
    }
  }

  getAllCharts(): Promise<ChartMetaInfo[]> {
    const charts = Object.values(this.state.charts)
      .filter((chart): chart is ChartData & { id: string | number } => !!chart.id)
      .map((chart) => ({
        id: chart.id,
        name: chart.name,
        symbol: chart.symbol,
        resolution: chart.resolution,
        timestamp: chart.timestamp,
      }));

    return Promise.resolve(charts);
  }

  async getChartContent(chartId: number | string): Promise<string> {
    const chart = this.state.charts[chartId];

    if (!chart || !chart.content) {
      throw new Error(`Chart ${chartId} not found`);
    }

    return chart.content;
  }

  getAllStudyTemplates(): Promise<StudyTemplateMetaInfo[]> {
    return Promise.resolve(Object.values(this.state.studyTemplates));
  }

  async getStudyTemplateContent(info: StudyTemplateMetaInfo): Promise<string> {
    const template = this.state.studyTemplates[info.name];

    if (!template) {
      throw new Error(`Study template ${info.name} not found`);
    }

    return template.content;
  }

  getDrawingTemplates(toolName: string): Promise<string[]> {
    const templates = Object.keys(this.state.drawingTemplates)
      .map((key) => key.split('/'))
      .filter((parts) => parts.length === 2 && parts[0] === toolName)
      .map((parts) => parts[1]);

    return Promise.resolve(templates);
  }

  async loadDrawingTemplate(toolName: string, templateName: string): Promise<string> {
    const key = drawingTemplateKey(toolName, templateName);
    const template = this.state.drawingTemplates[key];

    if (!template) {
      throw new Error(`Drawing template ${key} not found`);
    }

    return template;
  }

  getAllChartTemplates(): Promise<string[]> {
    return Promise.resolve(Object.keys(this.state.chartTemplates));
  }

  getChartTemplateContent(templateName: string): Promise<ChartTemplate> {
    const result: ChartTemplate = {};
    const template = this.state.chartTemplates[templateName];

    if (template) {
      result.content = structuredClone(template);
    }

    return Promise.resolve(result);
  }

  loadLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    _requestType: LineToolsAndGroupsLoadRequestType,
    _requestContext: LineToolsAndGroupsLoadRequestContext,
  ): Promise<Partial<LineToolsAndGroupsState> | null> {
    const rawSources = this.state.drawings[drawingKey(layoutId, chartId)];

    if (!rawSources) {
      return Promise.resolve(null);
    }

    const sources = new Map<string, unknown>();

    for (const [sourceId, sourceValue] of Object.entries(rawSources)) {
      sources.set(sourceId, sourceValue);
    }

    return Promise.resolve({ sources } as Partial<LineToolsAndGroupsState>);
  }

  saveChart(chartData: ChartData): Promise<string | number> {
    const id = chartData.id || createUUID();
    const saved: ChartData = {
      ...chartData,
      id,
      timestamp: Math.round(Date.now() / 1000),
    };
    this.state.charts[id] = saved;

    return Promise.resolve(id);
  }

  removeChart(id: string | number): Promise<void> {
    delete this.state.charts[id];

    return Promise.resolve();
  }

  saveStudyTemplate(template: StudyTemplateData): Promise<void> {
    this.state.studyTemplates[template.name] = template;

    return Promise.resolve();
  }

  removeStudyTemplate(info: StudyTemplateMetaInfo): Promise<void> {
    delete this.state.studyTemplates[info.name];

    return Promise.resolve();
  }

  saveDrawingTemplate(toolName: string, templateName: string, content: string): Promise<void> {
    this.state.drawingTemplates[drawingTemplateKey(toolName, templateName)] = content;

    return Promise.resolve();
  }

  removeDrawingTemplate(toolName: string, templateName: string): Promise<void> {
    delete this.state.drawingTemplates[drawingTemplateKey(toolName, templateName)];

    return Promise.resolve();
  }

  saveChartTemplate(newName: string, content: ChartTemplateContent): Promise<void> {
    this.state.chartTemplates[newName] = content;

    return Promise.resolve();
  }

  removeChartTemplate(templateName: string): Promise<void> {
    delete this.state.chartTemplates[templateName];

    return Promise.resolve();
  }

  saveLineToolsAndGroups(
    layoutId: string | undefined,
    chartId: string | number,
    state: LineToolsAndGroupsState,
  ): Promise<void> {
    const drawings = state.sources;
    const key = drawingKey(layoutId, chartId);

    if (!this.state.drawings[key]) {
      this.state.drawings[key] = {};
    }

    if (drawings) {
      for (const [id, value] of drawings) {
        if (value === null) {
          delete this.state.drawings[key][id];
        } else {
          this.state.drawings[key][id] = value as unknown;
        }
      }
    }

    return Promise.resolve();
  }
}
